/**
 * Zustand store holding live dashboard state.
 *
 * Five slices, deliberately separated so React selectors stay cheap:
 *
 * * ``connection`` — WebSocket lifecycle + transport stats
 * * ``building`` — bootstrap snapshot from ``/building/state``,
 *   refreshed on every reload
 * * ``feed`` — bounded ring buffer of recent envelopes for the
 *   "what's happening right now" panel
 * * ``pods`` — per-pod live state derived from incoming events
 *   (latest agent signals, PM decision, room, modifiers)
 * * ``ui`` — view router (building overview vs single pod room),
 *   audio toggle, etc.
 *
 * Stores **don't** call the API themselves; that's the job of hooks /
 * effects in ``main.tsx``.  This keeps the store pure-ish and easy to
 * test.
 */

import { create } from 'zustand';

import type { ConnectionState } from '@/api/ws';
import type { BuildingState, DashboardSnapshot, FloorId } from '@/api/rest';
import type { AnyEnvelope, EventActor, EventMood, SceneHint } from '@/events';
import { EventType, podIdFromSubject } from '@/events';

const FEED_MAX = 200;

/**
 * Persist the bounded feed to ``localStorage`` so a page reload picks
 * up where the last session left off.  Without this, fresh tabs start
 * with ``feed: []`` and rely on backend ``read_since`` to refill --
 * but the persisted cursor advances on every live event, so the
 * backlog replay almost always returns 0 envelopes for an idle reload.
 *
 *   * ``loadPersistedFeed()``  — best-effort restore on store init.
 *     Returns an empty array on any error (corrupted JSON, schema
 *     drift, quota exceeded earlier write, ...).
 *   * ``persistFeed(feed)``    — throttled write triggered after every
 *     ``appendEvent``.  We skip if the previous write was less than
 *     ~600 ms ago to avoid hammering localStorage during high-rate
 *     event bursts (cycle start + 6 agents fires ~20 envelopes/sec).
 *
 * Storage key is versioned so a future envelope-shape change can bump
 * the version and discard old payloads without manual cleanup.
 */
const FEED_STORAGE_KEY = 'hedgefundweb.feed.v1';
const FEED_PERSIST_MIN_INTERVAL_MS = 600;
let lastFeedPersistAt = 0;

function loadPersistedFeed(): AnyEnvelope[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(FEED_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Trim to bound + sanity-check each entry has the minimum keys
    // before we hand it to a reducer downstream.
    const sanitised = parsed
      .slice(-FEED_MAX)
      .filter(
        (e): e is AnyEnvelope =>
          typeof e === 'object' &&
          e !== null &&
          typeof (e as { id?: unknown }).id === 'string' &&
          typeof (e as { ts?: unknown }).ts === 'string' &&
          typeof (e as { type?: unknown }).type === 'string',
      );
    // Dedup at load so users carrying stale doubles in localStorage
    // (from before the cross-stream dedup landed) start a fresh tab
    // clean.  Cheap (Set-based) and only runs once on store init.
    return dedupeFeedByOriginId(sanitised);
  } catch {
    return [];
  }
}

function persistFeed(feed: AnyEnvelope[]): void {
  if (typeof window === 'undefined') return;
  const now = Date.now();
  if (now - lastFeedPersistAt < FEED_PERSIST_MIN_INTERVAL_MS) return;
  lastFeedPersistAt = now;
  try {
    window.localStorage.setItem(
      FEED_STORAGE_KEY,
      JSON.stringify(feed.slice(-FEED_MAX)),
    );
  } catch {
    /* quota / unavailable -- not worth surfacing */
  }
}

/**
 * Replay a persisted feed through every domain reducer to rebuild
 * pods / atmosphere / celebration / banners / leaderboard.  Mirrors
 * the ``appendEvent`` action body but operates on a fresh state seed
 * so it's safe to call before the store is created.  Skipped /
 * unknown event types are no-ops in the reducers, so this is safe
 * even when the feed has events the current code doesn't know.
 */
interface _HydratedDomains {
  pods: Record<string, PodRuntimeState>;
  atmosphere: AtmosphereState;
  celebration: CelebrationState;
  banners: BannersState;
  leaderboard: DashboardStore['leaderboard'];
}

function _rebuildStateFromFeed(feed: AnyEnvelope[]): _HydratedDomains {
  const pods: Record<string, PodRuntimeState> = {};
  let atmosphere: AtmosphereState = { ...EMPTY_ATMOSPHERE };
  let celebration: CelebrationState = { ...EMPTY_CELEBRATION };
  let banners: BannersState = { ...EMPTY_BANNERS };
  let leaderboard: DashboardStore['leaderboard'] = null;
  for (const env of feed) {
    const podId = podIdFromSubject(env.subject);
    if (podId) {
      const prev = pods[podId] ?? emptyPodRuntime(podId);
      pods[podId] = reducePodForEvent(prev, env);
    }
    atmosphere = reduceAtmosphereForEvent(atmosphere, env);
    const camera = env.scene_hint?.camera;
    if (camera && camera !== 'stay' && camera !== 'wide') {
      atmosphere = {
        ...atmosphere,
        cameraFocus: { kind: camera, podId, setAtMs: performance.now() },
      };
    }
    celebration = reduceCelebrationForEvent(celebration, env);
    banners = reduceBannersForEvent(banners, env);
    if (env.type === EventType.LEADERBOARD_COMPUTED) {
      const d = env.data;
      leaderboard = {
        period: String(d.period ?? 'weekly'),
        asOf: String(d.as_of ?? env.ts),
        metric: String(d.metric ?? 'utility_avg'),
        rows: (Array.isArray(d.rows) ? d.rows : []).map((row) => {
          const r = row as Record<string, unknown>;
          return {
            pod_id: String(r.pod_id ?? ''),
            rank: Number(r.rank ?? 0),
            value: Number(r.value ?? 0),
            name: typeof r.name === 'string' ? r.name : null,
            recentDelta: 0,
            changedAtMs: 0,
          };
        }),
      };
    }
  }
  return { pods, atmosphere, celebration, banners, leaderboard };
}

/**
 * Last few news.event_ingested headlines this pod's SA cares about.
 *
 * "Cares about" = the envelope's ``tickers`` list overlaps the
 * ``tickers`` set we were handed (typically the pod's positions plus
 * the SA's last ``topTickers``).  Empty ``tickers`` is treated as
 * "any" so a cold-start pod still gets *something* in the tooltip
 * rather than an empty box.
 *
 * Duplicate-flagged envelopes (``data.is_duplicate === true``) are
 * skipped — the dedupe layer in NewsIntelligence already flagged
 * these as repeats of an earlier wire and they would just clutter
 * the tooltip.
 *
 * Exported so a unit test can pin the filter behaviour and so the
 * Pixi tooltip + DOM PodRoomView share one source of truth.
 */
export interface RecentNewsForTooltip {
  id: string;
  title: string;
  tickers: string[];
  sentiment: number;
  ts: string;
}

export function recentSentimentNews(
  feed: readonly AnyEnvelope[],
  tickers: readonly string[],
  limit = 5,
): RecentNewsForTooltip[] {
  const wanted = new Set(tickers.map((t) => t.toUpperCase()));
  const want_all = wanted.size === 0;
  const out: RecentNewsForTooltip[] = [];
  // Newest first; bounded scan to avoid sweeping the whole feed when
  // SA has many positions.  We dedupe by ``event_id`` rather than
  // trusting backend's ``data.is_duplicate`` flag -- in production
  // every replayed news event came back duplicate-flagged, so the
  // tooltip stayed empty even when there was a fresh headline.
  const seenEventIds = new Set<string>();
  for (let i = feed.length - 1; i >= 0 && out.length < limit; i -= 1) {
    const env = feed[i];
    if (!env || env.type !== 'news.event_ingested') continue;
    const data = env.data as Record<string, unknown>;
    const eventId = typeof data.event_id === 'string' ? data.event_id : env.id;
    if (seenEventIds.has(eventId)) continue;
    seenEventIds.add(eventId);
    const envTickers = Array.isArray(data.tickers)
      ? (data.tickers as unknown[]).filter(
          (t): t is string => typeof t === 'string',
        )
      : [];
    const matches =
      want_all ||
      envTickers.some((t) => wanted.has(t.toUpperCase()));
    if (!matches) continue;
    out.push({
      id: env.id,
      title: typeof data.title === 'string' ? data.title : '',
      tickers: envTickers,
      sentiment: typeof data.sentiment === 'number' ? data.sentiment : 0,
      ts: env.ts,
    });
  }
  return out;
}

/**
 * Stable dedup key for an envelope.
 *
 * Three layers, picked in order:
 *
 * 1. ``origin_id`` — set by ``build_envelope`` on the backend and
 *    preserved through every publish/rehydrate path.  Stable across
 *    reads from ``events:all`` vs ``events:<subject>``.  This is the
 *    correct long-term identifier.
 *
 * 2. ``type + data.event_id`` — for envelope types that carry a
 *    stable upstream identifier in ``data`` (news.event_ingested
 *    stamps the NewsIntelligence event_id; ``news.ticker_sentiment_shift``
 *    has a ticker+window key in its data).  This fallback exists for
 *    envelopes still living in Redis from BEFORE the ``origin_id``
 *    field was rolled out: they don't carry ``origin_id`` so layer 1
 *    misses, but the upstream id is stable so we can still catch the
 *    cross-stream duplicate the dual-subscribe pattern produces.
 *
 * 3. ``envelope.id`` — last-resort fallback.  Catches within-stream
 *    duplicates (which is what the original dedup targeted) but not
 *    cross-stream ones.  Used for envelope types without a stable
 *    upstream identifier in ``data`` (most agent / pod / system
 *    events — those rarely hit the cross-stream path because they're
 *    only published once and read live).
 *
 * Plain ``envelope.id`` is the Redis Stream entry id of whichever
 * stream served the envelope, which differs between streams for the
 * same logical event and therefore can't dedup the cross-stream
 * duplicate the dashboard's dual subscribe (wildcard then narrow)
 * creates.
 */
export function envelopeDedupKey(
  env: Pick<AnyEnvelope, 'id' | 'origin_id' | 'type' | 'data'>,
): string {
  if (env.origin_id) return env.origin_id;
  // Stable upstream id in ``data`` — present on news.event_ingested
  // and any other type where the publisher provides a deterministic
  // key.  ``type``-prefixed so a coincidental event_id collision
  // between unrelated streams cannot wrongly mask one of them.
  const data = env.data as Record<string, unknown> | undefined;
  const upstreamId = data?.event_id;
  if (typeof upstreamId === 'string' && upstreamId) {
    return `upstream:${env.type}:${upstreamId}`;
  }
  return env.id;
}

/**
 * Tail-biased duplicate check by stable ``origin_id`` (fallback to
 * ``id`` for legacy envelopes).
 *
 * Both the WebSocket reconnect path and the REST resync path can
 * deliver an envelope the store already has — server's
 * ``read_since(sinceId)`` window naturally overlaps our persisted
 * cursor on most reload cycles, and ``onResync`` can re-feed events
 * racy with the live socket.  We scan back through ``scanDepth``
 * entries (a recently-arrived dup is almost always near the tail)
 * to keep this O(scanDepth) even when the feed is full.  Exported so
 * the unit test can pin behaviour without going through the full
 * Zustand store.
 */
export function feedHasDuplicateId(
  feed: readonly AnyEnvelope[],
  envOrId: string | Pick<AnyEnvelope, 'id' | 'origin_id' | 'type' | 'data'>,
  scanDepth = 64,
): boolean {
  const key = typeof envOrId === 'string' ? envOrId : envelopeDedupKey(envOrId);
  const start = Math.max(0, feed.length - scanDepth);
  for (let i = feed.length - 1; i >= start; i -= 1) {
    const candidate = feed[i];
    if (!candidate) continue;
    if (envelopeDedupKey(candidate) === key) return true;
  }
  return false;
}

/**
 * Remove duplicates by ``origin_id`` from a feed, keeping the LAST
 * occurrence of each key.  Used at ``loadPersistedFeed`` time so a
 * user whose localStorage was written by an older client (no dedup
 * or stream-id-only dedup) starts a fresh tab clean rather than
 * carrying the stale doubles into every render.
 */
export function dedupeFeedByOriginId(feed: AnyEnvelope[]): AnyEnvelope[] {
  if (feed.length < 2) return feed;
  const seen = new Set<string>();
  const out: AnyEnvelope[] = [];
  // Walk newest-first, keep first sight; reverse at the end to keep
  // chronological order.
  for (let i = feed.length - 1; i >= 0; i -= 1) {
    const env = feed[i];
    if (!env) continue;
    const key = envelopeDedupKey(env);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(env);
  }
  return out.reverse();
}

const SIGNAL_BUBBLE_MAX_AGE_MS = 8_000;
const PM_BUBBLE_MAX_AGE_MS = 12_000;

/** What we know about a single agent inside a pod's room. */
export interface AgentRuntimeState {
  actor: EventActor | string;
  /** Visible thinking ``...`` state — toggled by thinking_started/completed/signal. */
  thinking: boolean;
  /** Last emitted scalar signal in [-1, 1]. */
  lastSignal: number | null;
  /** Last reported confidence in [0, 1]. */
  lastConfidence: number | null;
  /** Top 3 tickers from the last signal. */
  topTickers: string[];
  /** Mood from the latest scene_hint, if any. */
  mood: EventMood | string | null;
  /** Intensity from the latest scene_hint (``low | medium | high | critical``).
   *  Drives animation-speed / glow multipliers in the Pixi scenes so a
   *  critical risk event feels visibly more urgent than a low-key
   *  idle action.  ``null`` means "no hint, use the medium default". */
  intensity: string | null;
  /** ISO timestamp of the latest meaningful update (for bubble fade-out). */
  lastUpdatedAt: string | null;
  /** Latency of the last LLM call in ms. */
  latencyMs: number | null;
  /** Most recent ambient phrase from ``agent.idle_action.text`` (the
   *  pre-rendered Russian sentence backend emits every ~45s --
   *  "листает свечные графики", "сверяет earnings", ...).  Survives
   *  across subsequent ``signal_emitted`` / ``thinking`` transitions
   *  so a hover-tooltip can always show "the last thing this agent
   *  said" even after the bubble has rotated to a signal payload. */
  lastPhrase: string | null;
  /** ISO timestamp of ``lastPhrase``.  ``null`` until the first
   *  ``agent.idle_action`` for this actor lands. */
  lastPhraseAt: string | null;
  /** Kind tag from ``agent.idle_action.kind`` -- e.g. ``"qa.indicators"``,
   *  ``"sa.tone"``.  Useful for the tooltip to show *what* the agent
   *  was doing (chart-reading, news-checking, ...) in addition to
   *  the prose. */
  lastPhraseKind: string | null;
}

/** PM-side state for a pod's room. */
export interface PodPmState {
  thinking: boolean;
  lastUiSummary: string | null;
  lastRationale: string | null;
  confidence: number | null;
  targetGross: number | null;
  intentsCount: number | null;
  decisionAt: string | null;
  /**
   * Latest portfolio composition the PM has committed to.  Top N
   * positions by absolute weight, sourced from
   * ``pm.decision_made.data.top_weights``.  ``weight`` can be
   * negative for shorts.  Backend bounds this at ~10 items.
   */
  topWeights: ReadonlyArray<{ ticker: string; weight: number }>;
}

/** Per-pod warning / error tracking (rejected orders + risk vetoes). */
export interface PodAlertState {
  /** Cumulative count of error-class events since the page mounted. */
  errorCount: number;
  /** Latest error -- ``null`` when the pod is clean. */
  last:
    | {
        kind:
          | 'order_rejected'
          | 'risk_veto'
          | 'order_retried'
          | 'gate_blocked';
        ts: string;
        ticker: string | null;
        reason: string;
      }
    | null;
}

/** Latest utility score components (for the wall ribbon). */
export interface PodUtilityState {
  total: number;
  components: Record<string, number>;
  version: string | null;
  recordedAt: string;
}

/** A single ``pod.nav_tick`` envelope reduced into the runtime store.
 *
 * Each pod cycle emits one of these; the sparkline keeps the last N
 * (see ``NAV_HISTORY_MAX``).  The richer ``positions[]`` array that
 * also rides on the same event is *not* stored here -- it goes to
 * ``PodRuntimeState.positions`` instead, because it represents
 * point-in-time state (latest wins) rather than a time series. */
export interface NavTick {
  /** ISO timestamp of the tick. */
  ts: string;
  /** NAV in account currency.  Economic NAV (broker NAV minus sandbox
   *  topups) when virtualization is active; raw broker NAV otherwise. */
  nav: number;
  /** Intraday return on the *economic* NAV scale (fraction, e.g. 0.0042
   *  = +0.42 %).  For sandbox pods with heavy topups this is amplified
   *  by the small denominator (a +2% broker day reads as +30%+ here).
   *  Prefer ``brokerScaleIntradayReturn`` for the headline percentage
   *  when it's available. */
  intradayReturn: number | null;
  /** Intraday PnL in account currency.  Identical on both scales -- the
   *  topup delta cancels across the day. */
  intradayPnl: number | null;
  /** Companion return on the *broker* NAV scale.  ``null`` when the
   *  broker hasn't been reached yet or the pod has no topups (in which
   *  case ``intradayReturn`` is already broker-truth and the two
   *  agree). */
  brokerScaleIntradayReturn: number | null;
  /** Raw broker NAV (cash + positions, no topup subtraction). */
  brokerNav: number | null;
}

/** Discipline-gate flag raised on a pod ("suspected_overtrading", etc.). */
export interface PodDisciplineFlag {
  code: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  detail: Record<string, unknown>;
  raisedAt: string;
}

/** Center-book overlay applied to a pod (capital scale adjustment). */
export interface PodCenterbookOverlay {
  podScale: number;
  grossBefore: number | null;
  grossAfter: number | null;
  appliedAt: string;
}

/** Single live position in a pod's book.
 *
 * Two upstream sources populate this shape:
 *
 * 1. Streaming ``position.opened`` / ``position.adjusted`` / ``position.closed``
 *    events (per-trade granularity, but only carries weight + qty).
 * 2. ``pod.nav_tick.positions[]`` -- a full broker-side snapshot
 *    refreshed every cycle.  Carries the richer ``price`` / ``notional``
 *    / ``side`` / ``figi`` fields.
 *
 * Fields that source #1 doesn't know are nullable here so the same
 * type can absorb both inputs; readers should fall back to deriving
 * (e.g. ``side = weight < 0 ? 'short' : 'long'``) when ``price`` or
 * ``notional`` is null. */
export interface PodPosition {
  ticker: string;
  /** Portfolio weight in [-1, 1].  Negative = short. */
  weight: number;
  /** Signed share/contract count.  Negative = short.  Null when only a
   * ``position.*`` event was received without a qty hint. */
  qty: number | null;
  /** Current mark-to-market price per share, in account currency.
   * Null when only weight-stream events were received. */
  price: number | null;
  /** Signed notional = ``qty * price``.  Null until both qty and price
   * are known.  Sign mirrors qty (shorts negative). */
  notional: number | null;
  /** Explicit ``"long"`` / ``"short"``.  Backend sends this in
   * nav_tick; for weight-only updates we derive from weight sign. */
  side: 'long' | 'short' | null;
  /** Tinkoff FIGI when available -- useful for ticker disambiguation
   * across markets. */
  figi: string | null;
  /** Broker instrument class (``share``, ``etf``, ``future``, ...). */
  instrumentType: string | null;
  /** ISO timestamp this position first opened. */
  openedAt: string;
  /** ISO timestamp of the most recent change (open/adjust/snapshot). */
  lastUpdatedAt: string;
}

/** Center-book capital reallocation -- fires during daily close when
 *  capital is shifted between pods.  Independent of ``overlay_applied``
 *  which is a same-day exposure tweak. */
export interface PodCenterbookAllocation {
  capitalBefore: number | null;
  capitalAfter: number;
  /** Delta as a fraction (e.g. +0.15 = +15 % capital). */
  deltaPct: number | null;
  reason: string | null;
  changedAt: string;
}

/** Latest PolicyUpdater action observed on a pod.  Surfaces the
 * shadow/applied/rolled_back lifecycle so the PolicyUpdater character
 * isn't invisible between cycles. */
export interface PodPolicyAction {
  kind: 'no_change' | 'shadow_recommended' | 'applied' | 'rolled_back';
  triggerRule: string;
  paramChanged: string | null;
  valueAfter: unknown;
  auditId: number | null;
  at: string;
}

/** Per-pod aggregated state. */
export interface PodRuntimeState {
  podId: string;
  /** Floor id (room) derived from the latest ``risk.state_changed`` / bootstrap. */
  floor: FloorId | null;
  riskState: 'active' | 'watch' | 'reduced' | 'frozen' | null;
  haircutPct: number | null;
  agents: Partial<Record<string, AgentRuntimeState>>;
  pm: PodPmState;
  utility: PodUtilityState | null;
  /** Last N order events for the trading floor animation. */
  recentOrders: Array<{
    id: string;
    type: 'submitted' | 'filled' | 'rejected';
    ticker: string;
    ts: string;
    detail: string;
  }>;
  /** Last cycle trace_id; used to scope room visuals. */
  cycleTraceId: string | null;
  /** Latest scene_hint from the most recent event addressed to this pod. */
  lastSceneHint: SceneHint | null;
  /** Pod-level alerts (rejected orders, risk vetoes, retries). */
  alerts: PodAlertState;
  /** Rolling NAV-tick history (newest last), bounded at 16 points.  Used
   * for the per-pod sparkline on the building view + intraday PnL ribbon. */
  navHistory: readonly NavTick[];
  /** Latest ``discipline.flag_raised`` -- null when the gate hasn't fired. */
  disciplineFlag: PodDisciplineFlag | null;
  /** Latest ``centerbook.overlay_applied`` payload (scale + bookend grosses). */
  centerbook: PodCenterbookOverlay | null;
  /** Latest ``centerbook.allocation_changed`` payload (capital
   *  redistribution during daily close).  Distinct from ``centerbook``
   *  above which is an intra-day exposure scale. */
  centerbookAllocation: PodCenterbookAllocation | null;
  /** Live position book -- keyed by ticker.  Two upstream sources keep
   *  it fresh:
   *
   *  * Streaming ``position.opened`` / ``position.adjusted`` /
   *    ``position.closed`` events (per-trade granularity).
   *  * The ``positions[]`` array on every ``pod.nav_tick`` -- a full
   *    broker snapshot, including the long tail of small positions
   *    inherited from earlier cycles that the streaming path may have
   *    missed (e.g. when the dashboard reconnects with a 200-event
   *    replay window).
   *
   *  When this map is non-empty the Portfolio panel prefers it over
   *  the ``pm.decision_made.top_weights`` snapshot. */
  positions: Record<string, PodPosition>;
  /** Latest broker-side cash balance in account currency.  Set by
   *  ``pod.nav_tick.cash_balance``.  Null while waiting for first tick. */
  cashBalance: number | null;
  /** Latest ``gross_exposure`` (``Σ |notional| / capital``) from
   *  nav_tick.  Independent of the per-position weights so the UI can
   *  surface it as a header chip. */
  grossExposure: number | null;
  /** Latest ``net_exposure`` from nav_tick (signed). */
  netExposure: number | null;
  /** Broker source label (e.g. ``tinkoff_get_portfolio``,
   *  ``market_snapshot_fallback``).  ``null`` until first tick.
   *  When the source is the fallback the UI should hint that
   *  ``positions`` may be stale or synthesised. */
  brokerSource: string | null;
  /** ISO timestamp of the latest broker snapshot (``pod.nav_tick.as_of``).
   *  Used by the UI to render a "snapshot at HH:MM" caption next to
   *  the Portfolio card. */
  positionsAsOf: string | null;
  /** ``cycle.started`` -> set; ``cycle.completed`` -> cleared.  Drives the
   * "cycle running" border highlight on the building view. */
  cycleInFlight: boolean;
  /** Latest PolicyUpdater action -- null when no policy event has landed. */
  policyAction: PodPolicyAction | null;
  /** In-flight floor transition.  Set by ``room.exited``, cleared by
   *  the matching ``room.entered``.  ``null`` when the pod is settled
   *  in its current room.  The building view paints a small "moving"
   *  arrow badge on pods with an active transition so the staircase
   *  shuffle is visible from the exterior. */
  floorTransition: {
    /** The room being left -- ``next.floor`` at the time of the event. */
    from: FloorId | null;
    /** ISO timestamp of the originating ``room.exited``. */
    startedAt: string;
  } | null;
}

const EMPTY_PM_STATE: PodPmState = {
  thinking: false,
  lastUiSummary: null,
  lastRationale: null,
  confidence: null,
  targetGross: null,
  intentsCount: null,
  decisionAt: null,
  topWeights: [],
};

const EMPTY_ALERTS: PodAlertState = { errorCount: 0, last: null };

function emptyPodRuntime(podId: string, floor: FloorId | null = null): PodRuntimeState {
  return {
    podId,
    floor,
    riskState: null,
    haircutPct: null,
    agents: {},
    pm: { ...EMPTY_PM_STATE },
    utility: null,
    recentOrders: [],
    cycleTraceId: null,
    lastSceneHint: null,
    alerts: { ...EMPTY_ALERTS },
    navHistory: [],
    disciplineFlag: null,
    centerbook: null,
    cycleInFlight: false,
    policyAction: null,
    floorTransition: null,
    centerbookAllocation: null,
    positions: {},
    cashBalance: null,
    grossExposure: null,
    netExposure: null,
    brokerSource: null,
    positionsAsOf: null,
  };
}

const NAV_HISTORY_MAX = 16;

/** Top-level UI view router state.  Will grow with frozen-detail / friday modal. */
export type UiView = { kind: 'building' } | { kind: 'pod_room'; podId: string };

export type BuildingRenderer = 'pixi' | 'dom';

export type WeatherKind = 'sunny' | 'cloudy' | 'storm' | 'windy' | 'snow' | 'fog';

export type SunPhase =
  | 'predawn'
  | 'morning'
  | 'noon'
  | 'afternoon'
  | 'evening'
  | 'night'
  | 'weekend_moon'
  | 'holiday_moon';

/**
 * Atmosphere overlay state, driven by ``building.weather_changed`` and
 * ``building.sun_position`` events that the backend emits every
 * atmosphere tick.  Used by the Pixi scene to tint the sky, the building
 * windows, and the weather particles.
 */
export interface AtmosphereState {
  weather: WeatherKind | null;
  previousWeather: WeatherKind | null;
  marketRegime: string | null;
  regimeConfidence: number | null;
  sunPhase: SunPhase | null;
  sunElevationDeg: number | null;
  sunAzimuthDeg: number | null;
  isMarketOpen: boolean | null;
  minutesToNextBoundary: number | null;
  lastUpdatedAt: string | null;
  /**
   * Latest ``scene_hint.camera`` directive.  Backend marks especially
   * dramatic envelopes ("focus_on_target") with a target subject so
   * the UI can spotlight one pod.  We stash the pod id + a
   * ``performance.now()`` deadline; scenes draw a halo on the target
   * and dim non-target pods until the deadline passes.  ``null`` =
   * no recent camera directive, render normally.
   */
  cameraFocus: {
    kind: string;
    podId: string | null;
    /** ``performance.now()`` timestamp when the directive landed. */
    setAtMs: number;
  } | null;
  /**
   * Daily-close lifecycle: ``started`` when the night ritual begins
   * (~21:00 UTC), ``completed`` once the leaderboard preview ships,
   * ``null`` otherwise.  Drives the night-ritual banner + a darker
   * window tint while the ritual is in progress.
   */
  dailyClose: {
    phase: 'started' | 'completed';
    /** ISO timestamp from the originating event. */
    at: string;
    /** Optional summary text from the completed envelope (pods closed,
     *  net P&L, top pod, etc).  Backend ships these as free fields on
     *  the ``completed`` payload. */
    summary: string | null;
  } | null;
}

/**
 * Two short-lived top-of-page banner slots:
 *   * ``session`` -- "Открытие через 4 мин" / "Закрытие через 2 мин"
 *     from ``building.session_warning``; cleared by the matching
 *     ``session_opened`` / ``session_closed``.
 *   * ``factorCrowd`` -- "factor crowd detected" red warning from
 *     ``building.factor_crowd_detected``; cleared by
 *     ``building.factor_crowd_relieved``.
 */
export interface BannersState {
  session: {
    boundaryKind: 'open' | 'close';
    boundaryAt: string;
    minutesUntil: number;
  } | null;
  factorCrowd: {
    maxCorrelation: number;
    threshold: number;
    affectedPods: readonly string[];
    asOf: string;
  } | null;
}

export const EMPTY_BANNERS: BannersState = {
  session: null,
  factorCrowd: null,
};

export function reduceBannersForEvent(
  prev: BannersState,
  env: AnyEnvelope,
): BannersState {
  if (env.type === EventType.BUILDING_SESSION_WARNING) {
    const d = env.data;
    return {
      ...prev,
      session: {
        boundaryKind: (d.boundary_kind as 'open' | 'close') ?? 'open',
        boundaryAt: String(d.boundary_at ?? env.ts),
        minutesUntil: Number(d.minutes_until_boundary ?? 0),
      },
    };
  }
  if (
    env.type === EventType.BUILDING_SESSION_OPENED ||
    env.type === EventType.BUILDING_SESSION_CLOSED
  ) {
    // The bell rang -- warning is no longer relevant.
    return { ...prev, session: null };
  }
  if (env.type === EventType.BUILDING_FACTOR_CROWD_DETECTED) {
    const d = env.data;
    return {
      ...prev,
      factorCrowd: {
        maxCorrelation: Number(d.max_correlation ?? 0),
        threshold: Number(d.threshold ?? 0),
        affectedPods: Array.isArray(d.affected_pods)
          ? (d.affected_pods as string[])
          : [],
        asOf: String(d.as_of ?? env.ts),
      },
    };
  }
  if (env.type === EventType.BUILDING_FACTOR_CROWD_RELIEVED) {
    return { ...prev, factorCrowd: null };
  }
  return prev;
}

/** Friday celebration overlay state -- driven by ``celebration.*`` events. */
export interface CelebrationState {
  /** True while a celebration is currently being shown. */
  active: boolean;
  /** ISO timestamp of the ``celebration.weekly_started`` event. */
  startedAt: string | null;
  winnerPodId: string | null;
  winnerName: string | null;
  metric: string | null;
  /** Best metric value of the winner. */
  value: number | null;
  /** Top-3 podium, populated by ``celebration.podium`` events. */
  podium: ReadonlyArray<{
    rank: 1 | 2 | 3;
    pod_id: string;
    name: string | null;
    value: number;
  }>;
}

export const EMPTY_CELEBRATION: CelebrationState = {
  active: false,
  startedAt: null,
  winnerPodId: null,
  winnerName: null,
  metric: null,
  value: null,
  podium: [],
};

/**
 * Pure reducer: take the previous celebration state and apply one
 * envelope.  Same shape and discipline as ``reduceAtmosphereForEvent``
 * so the suite can exercise it without a DOM.
 */
export function reduceCelebrationForEvent(
  prev: CelebrationState,
  env: AnyEnvelope,
): CelebrationState {
  if (env.type === EventType.CELEBRATION_WEEKLY_STARTED) {
    const d = env.data;
    return {
      active: true,
      startedAt: env.ts,
      winnerPodId: (d.winner_pod_id as string) ?? null,
      winnerName: (d.winner_name as string) ?? null,
      metric: (d.metric as string) ?? null,
      value: d.value != null ? Number(d.value) : null,
      podium: [],
    };
  }
  if (env.type === EventType.CELEBRATION_PODIUM) {
    if (!prev.active) return prev; // ignore stray podium pings
    const d = env.data;
    const rank = Number(d.rank);
    if (rank < 1 || rank > 3) return prev;
    const next = prev.podium.filter((p) => p.rank !== rank);
    next.push({
      rank: rank as 1 | 2 | 3,
      pod_id: String(d.pod_id ?? ''),
      name: (d.name as string) ?? null,
      value: Number(d.value ?? 0),
    });
    next.sort((a, b) => a.rank - b.rank);
    return { ...prev, podium: next };
  }
  if (env.type === EventType.CELEBRATION_WEEKLY_ENDED) {
    return { ...EMPTY_CELEBRATION };
  }
  return prev;
}

export const EMPTY_ATMOSPHERE: AtmosphereState = {
  weather: null,
  previousWeather: null,
  marketRegime: null,
  regimeConfidence: null,
  sunPhase: null,
  sunElevationDeg: null,
  sunAzimuthDeg: null,
  isMarketOpen: null,
  minutesToNextBoundary: null,
  lastUpdatedAt: null,
  dailyClose: null,
  cameraFocus: null,
};

/**
 * Pure reducer: take the previous atmosphere state and apply one
 * envelope.  Returns the same reference unchanged when the envelope is
 * unrelated, so React selectors stay shallow-stable.
 */
export function reduceAtmosphereForEvent(
  prev: AtmosphereState,
  env: AnyEnvelope,
): AtmosphereState {
  if (env.type === EventType.BUILDING_WEATHER_CHANGED) {
    const d = env.data;
    return {
      ...prev,
      weather: (d.weather_kind as WeatherKind) ?? prev.weather,
      previousWeather:
        (d.previous_weather_kind as WeatherKind | null) ?? prev.previousWeather,
      marketRegime: (d.market_regime as string | null) ?? prev.marketRegime,
      regimeConfidence:
        d.regime_confidence != null
          ? Number(d.regime_confidence)
          : prev.regimeConfidence,
      lastUpdatedAt: env.ts,
    };
  }
  if (env.type === EventType.BUILDING_DAILY_CLOSE_STARTED) {
    return {
      ...prev,
      dailyClose: { phase: 'started', at: env.ts, summary: null },
      lastUpdatedAt: env.ts,
    };
  }
  if (env.type === EventType.BUILDING_DAILY_CLOSE_COMPLETED) {
    const d = env.data;
    // Backend ships a handful of optional summary fields -- net P&L,
    // pod count, top pod name.  Stitch them into a short sentence.
    const podsClosed =
      typeof d.pods_closed === 'number' ? Number(d.pods_closed) : null;
    const totalPnl =
      typeof d.total_pnl === 'number' ? Number(d.total_pnl) : null;
    const topPod =
      typeof d.top_pod_name === 'string'
        ? d.top_pod_name
        : typeof d.top_pod_id === 'string'
          ? d.top_pod_id
          : null;
    const parts: string[] = [];
    if (podsClosed != null) parts.push(`${podsClosed} pods closed`);
    if (totalPnl != null) {
      const pnlMln = totalPnl / 1_000_000;
      parts.push(`net ${pnlMln >= 0 ? '+' : ''}${pnlMln.toFixed(2)}M ₽`);
    }
    if (topPod) parts.push(`top: ${topPod}`);
    return {
      ...prev,
      dailyClose: {
        phase: 'completed',
        at: env.ts,
        summary: parts.length > 0 ? parts.join(' · ') : null,
      },
      lastUpdatedAt: env.ts,
    };
  }
  if (env.type === EventType.BUILDING_SUN_POSITION) {
    const d = env.data;
    return {
      ...prev,
      sunPhase: (d.phase as SunPhase) ?? prev.sunPhase,
      sunElevationDeg:
        d.elevation_degrees != null
          ? Number(d.elevation_degrees)
          : prev.sunElevationDeg,
      sunAzimuthDeg:
        d.azimuth_degrees != null ? Number(d.azimuth_degrees) : prev.sunAzimuthDeg,
      isMarketOpen:
        typeof d.is_market_open === 'boolean' ? d.is_market_open : prev.isMarketOpen,
      minutesToNextBoundary:
        d.minutes_to_next_boundary != null
          ? Number(d.minutes_to_next_boundary)
          : prev.minutesToNextBoundary,
      lastUpdatedAt: env.ts,
    };
  }
  return prev;
}

export interface DashboardStore {
  connection: ConnectionState;
  lastError: string | null;
  eventsReceived: number;
  feed: AnyEnvelope[];
  building: BuildingState | null;
  buildingFetchedAt: string | null;
  pods: Record<string, PodRuntimeState>;
  leaderboard: {
    period: string;
    asOf: string;
    metric: string;
    rows: Array<{
      pod_id: string;
      rank: number;
      value: number;
      name?: string | null;
      /** Recent rank delta: positive = climbed, negative = fell.
       *  Set by ``leaderboard.rank_changed``; cleared by the UI after
       *  the indicator fade window expires. */
      recentDelta: number;
      /** ``performance.now()`` timestamp of the latest rank change.
       *  ``0`` means the row has never been moved on the live board. */
      changedAtMs: number;
    }>;
  } | null;
  atmosphere: AtmosphereState;
  celebration: CelebrationState;
  banners: BannersState;
  view: UiView;
  buildingRenderer: BuildingRenderer;
  /** Audio dispatcher state -- starts muted so we don't surprise visitors. */
  audioMuted: boolean;

  setConnection: (state: ConnectionState) => void;
  setError: (err: unknown) => void;
  appendEvent: (envelope: AnyEnvelope) => void;
  setBuilding: (state: BuildingState) => void;
  /** Drop the authoritative server snapshot into the store in one go.
   *  Used on mount instead of replaying the persisted event-feed
   *  window -- the snapshot carries positions, PM decision, utility,
   *  agent phrases, nav history, risk/discipline/centerbook/policy
   *  state etc. that the event-replay path can't reconstruct when
   *  the originating events fell out of the local window. */
  hydrateFromSnapshot: (snapshot: DashboardSnapshot) => void;
  selectPod: (podId: string) => void;
  goToBuilding: () => void;
  setBuildingRenderer: (r: BuildingRenderer) => void;
  setAudioMuted: (muted: boolean) => void;
  reset: () => void;
}

/**
 * Pure reducer: take the previous pod state and apply one envelope.
 * Exported separately so it stays unit-testable.
 */
export function reducePodForEvent(
  prev: PodRuntimeState,
  env: AnyEnvelope,
): PodRuntimeState {
  // Always remember the latest scene hint so the UI can react to mood/audio.
  const next: PodRuntimeState = {
    ...prev,
    lastSceneHint: env.scene_hint ?? prev.lastSceneHint,
    cycleTraceId: env.trace_id ?? prev.cycleTraceId,
  };

  switch (env.type) {
    case EventType.AGENT_THINKING_STARTED: {
      const actor = String(env.data.actor ?? 'qa');
      const existing = prev.agents[actor];
      const agent: AgentRuntimeState = {
        actor,
        thinking: true,
        lastSignal: existing?.lastSignal ?? null,
        lastConfidence: existing?.lastConfidence ?? null,
        topTickers: existing?.topTickers ?? [],
        mood: 'thinking',
        intensity: env.scene_hint?.intensity ?? existing?.intensity ?? null,
        lastUpdatedAt: env.ts,
        latencyMs: existing?.latencyMs ?? null,
        // Preserve the last ambient phrase across thinking transitions
        // -- the hover tooltip should still show "the last thing this
        // agent said" even while it's thinking about the next cycle.
        lastPhrase: existing?.lastPhrase ?? null,
        lastPhraseAt: existing?.lastPhraseAt ?? null,
        lastPhraseKind: existing?.lastPhraseKind ?? null,
      };
      next.agents = { ...prev.agents, [actor]: agent };
      // PM thinking_started gets its own treatment so the PM bubble
      // doesn't get clobbered by a regular agent update.
      if (actor === 'pm') next.pm = { ...prev.pm, thinking: true };
      return next;
    }
    case EventType.AGENT_SIGNAL_EMITTED: {
      const actor = String(env.data.actor ?? 'qa');
      const existing = prev.agents[actor];
      const top = Array.isArray(env.data.top_tickers)
        ? (env.data.top_tickers as string[]).slice(0, 3)
        : existing?.topTickers ?? [];
      const agent: AgentRuntimeState = {
        actor,
        thinking: false,
        lastSignal: Number(env.data.signal ?? 0),
        lastConfidence:
          env.data.confidence != null ? Number(env.data.confidence) : null,
        topTickers: top,
        mood: env.scene_hint?.mood ?? 'idle',
        intensity: env.scene_hint?.intensity ?? null,
        lastUpdatedAt: env.ts,
        latencyMs: env.data.latency_ms != null ? Number(env.data.latency_ms) : null,
        // Same preservation as thinking_started: a signal_emitted
        // doesn't invalidate the last ambient phrase; both are useful
        // hover-context for the operator.
        lastPhrase: existing?.lastPhrase ?? null,
        lastPhraseAt: existing?.lastPhraseAt ?? null,
        lastPhraseKind: existing?.lastPhraseKind ?? null,
      };
      next.agents = { ...prev.agents, [actor]: agent };
      return next;
    }
    case EventType.AGENT_IDLE_ACTION: {
      // Ambient layer: backend emits one per ~45 s per pod with a
      // pre-formatted Russian sentence ("листает свечные графики",
      // "читает RBC", ...).  Skip if the agent is currently thinking
      // -- don't overwrite an active LLM bubble.
      const actor = String(env.data.actor ?? 'qa');
      const existing = prev.agents[actor];
      const wasThinking = existing?.thinking ?? false;
      if (wasThinking) return next;
      const text = String(env.data.text ?? '');
      if (!text) return next;
      const kind = env.data.kind != null ? String(env.data.kind) : null;
      const agent: AgentRuntimeState = {
        actor,
        thinking: false,
        lastSignal: existing?.lastSignal ?? null,
        lastConfidence: existing?.lastConfidence ?? null,
        // Hijack ``topTickers`` to ferry the ambient sentence to the
        // existing bubble renderer.  A single-element array of free
        // text reads naturally as a one-line bubble.
        topTickers: [text],
        mood: (env.data.mood as string) ?? env.scene_hint?.mood ?? 'idle',
        // Idle actions are by definition low-intensity unless the
        // hint explicitly says otherwise.
        intensity: env.scene_hint?.intensity ?? 'low',
        lastUpdatedAt: env.ts,
        latencyMs: existing?.latencyMs ?? null,
        // ``lastPhrase`` is the dedicated source of truth for "the last
        // thing this agent said" -- separate from ``topTickers`` which
        // gets clobbered by signal_emitted's actual top-3 list.
        lastPhrase: text,
        lastPhraseAt: env.ts,
        lastPhraseKind: kind,
      };
      next.agents = { ...prev.agents, [actor]: agent };
      return next;
    }
    case EventType.PM_DECISION_MADE: {
      const rawWeights = Array.isArray(env.data.top_weights)
        ? (env.data.top_weights as Array<Record<string, unknown>>)
        : [];
      const topWeights = rawWeights
        .map((row) => ({
          ticker: String(row.ticker ?? ''),
          weight: Number(row.weight ?? 0),
        }))
        .filter((row) => row.ticker.length > 0)
        // Newest first, top |weight| wins; backend usually caps at 10.
        .sort((a, b) => Math.abs(b.weight) - Math.abs(a.weight))
        .slice(0, 10);
      next.pm = {
        thinking: false,
        lastUiSummary: (env.data.ui_summary as string) ?? null,
        lastRationale: (env.data.rationale as string) ?? null,
        confidence: env.data.confidence != null ? Number(env.data.confidence) : null,
        targetGross:
          env.data.target_gross != null ? Number(env.data.target_gross) : null,
        intentsCount:
          env.data.intents_count != null ? Number(env.data.intents_count) : null,
        decisionAt: env.ts,
        topWeights,
      };
      return next;
    }
    case EventType.PM_NO_TRADE_DECISION: {
      next.pm = {
        ...prev.pm,
        thinking: false,
        lastUiSummary: (env.data.ui_summary as string) ?? prev.pm.lastUiSummary,
        decisionAt: env.ts,
        targetGross: 0,
        intentsCount: 0,
      };
      return next;
    }
    case EventType.RISK_STATE_CHANGED: {
      const to = String(env.data.to ?? '').toLowerCase();
      const haircut = env.data.haircut_pct != null ? Number(env.data.haircut_pct) : null;
      next.riskState = (to as PodRuntimeState['riskState']) ?? prev.riskState;
      next.haircutPct = haircut;
      return next;
    }
    case EventType.ROOM_EXITED: {
      // ``exited`` lands first, ``entered`` 1-2 s later.  Record the
      // origin so scenes can paint a "moving out" badge until the
      // pair completes.
      const room = String(env.data.room ?? '');
      next.floorTransition = {
        from: (room as FloorId) ?? prev.floor,
        startedAt: env.ts,
      };
      return next;
    }
    case EventType.ROOM_ENTERED: {
      const room = String(env.data.room ?? '');
      next.floor = (room as FloorId) ?? prev.floor;
      // Pair completes -- clear the moving badge.
      next.floorTransition = null;
      return next;
    }
    case EventType.UTILITY_RECORDED: {
      next.utility = {
        total: Number(env.data.total ?? 0),
        components:
          (env.data.components as Record<string, number> | undefined) ?? {},
        version: (env.data.version as string) ?? null,
        recordedAt: env.ts,
      };
      return next;
    }
    case EventType.ORDER_SUBMITTED:
    case EventType.ORDER_FILLED:
    case EventType.ORDER_REJECTED: {
      const ticker = String(env.data.ticker ?? '');
      const detail =
        env.type === EventType.ORDER_FILLED
          ? `${env.data.side ?? ''} @ ${env.data.price ?? '—'}`
          : env.type === EventType.ORDER_REJECTED
            ? `rejected: ${env.data.reason ?? ''}`
            : `${env.data.side ?? ''}`;
      const kind: 'submitted' | 'filled' | 'rejected' =
        env.type === EventType.ORDER_FILLED
          ? 'filled'
          : env.type === EventType.ORDER_REJECTED
            ? 'rejected'
            : 'submitted';
      next.recentOrders = [
        { id: env.id, type: kind, ticker, ts: env.ts, detail },
        ...prev.recentOrders,
      ].slice(0, 25);
      // ``order.rejected`` is an error event -- track count + last reason.
      if (env.type === EventType.ORDER_REJECTED) {
        next.alerts = {
          errorCount: prev.alerts.errorCount + 1,
          last: {
            kind: 'order_rejected',
            ts: env.ts,
            ticker: ticker || null,
            reason: String(env.data.reason ?? 'unknown rejection'),
          },
        };
      }
      return next;
    }
    case EventType.ORDER_RETRIED: {
      // Retries hint at flaky broker connectivity -- surface in alerts.
      next.alerts = {
        errorCount: prev.alerts.errorCount + 1,
        last: {
          kind: 'order_retried',
          ts: env.ts,
          ticker: String(env.data.ticker ?? '') || null,
          reason: 'order retried',
        },
      };
      return next;
    }
    case EventType.RISK_VERDICT_RECEIVED: {
      // Vetoes (approved=false) are the pod's risk-side error class.
      if (env.data.approved === false) {
        const reasons = Array.isArray(env.data.reasons)
          ? (env.data.reasons as string[]).join(', ')
          : 'risk veto';
        next.alerts = {
          errorCount: prev.alerts.errorCount + 1,
          last: {
            kind: 'risk_veto',
            ts: env.ts,
            ticker: null,
            reason: reasons || 'risk veto',
          },
        };
      }
      return next;
    }
    case EventType.POD_NAV_TICK: {
      const d = env.data;
      const tick: NavTick = {
        ts: env.ts,
        nav: Number(d.nav ?? 0),
        intradayReturn:
          d.intraday_return != null ? Number(d.intraday_return) : null,
        intradayPnl: d.intraday_pnl != null ? Number(d.intraday_pnl) : null,
        brokerScaleIntradayReturn:
          d.broker_scale_intraday_return != null
            ? Number(d.broker_scale_intraday_return)
            : null,
        brokerNav: d.broker_nav != null ? Number(d.broker_nav) : null,
      };
      next.navHistory = [...prev.navHistory, tick].slice(-NAV_HISTORY_MAX);

      // Refresh book metadata regardless of whether ``positions[]``
      // is present -- cash/gross/net are useful on their own.
      if (d.cash_balance != null) next.cashBalance = Number(d.cash_balance);
      if (d.gross_exposure != null) next.grossExposure = Number(d.gross_exposure);
      if (d.net_exposure != null) next.netExposure = Number(d.net_exposure);
      if (typeof d.broker_source === 'string') next.brokerSource = d.broker_source;

      // ``positions[]`` rides on every tick when broker data is
      // available.  We REPLACE the positions map rather than merging
      // because the broker snapshot is authoritative for "what the
      // pod holds right now" -- streaming ``position.*`` events would
      // resume populating the map after this tick.
      //
      // Two failure modes to guard against:
      // * ``positions`` missing or not an array -> backend ran in
      //   fallback mode; keep the existing map so the UI doesn't
      //   blank out the Portfolio card.
      // * ``positions`` empty array -> ambiguous (genuinely flat pod
      //   vs. broker offline).  We treat ``[]`` the same as missing
      //   to avoid surprise blanking; if a pod is actually flat the
      //   UI will catch that via per-position ``position.closed``
      //   events that delete entries individually.
      const rawPositions = d.positions;
      if (Array.isArray(rawPositions) && rawPositions.length > 0) {
        const asOf = typeof d.as_of === 'string' ? d.as_of : env.ts;
        const nextPositions: Record<string, PodPosition> = {};
        for (const raw of rawPositions) {
          if (!raw || typeof raw !== 'object') continue;
          const row = raw as Record<string, unknown>;
          const ticker = String(row.ticker ?? '').trim().toUpperCase();
          if (!ticker) continue;
          const qty = row.qty != null ? Number(row.qty) : null;
          const price = row.price != null ? Number(row.price) : null;
          const notional = row.notional != null ? Number(row.notional) : null;
          const weight = Number(row.weight ?? 0);
          const sideRaw = String(row.side ?? '').toLowerCase();
          const side: 'long' | 'short' | null =
            sideRaw === 'long' || sideRaw === 'short'
              ? sideRaw
              : (qty != null ? qty : weight) < 0
                ? 'short'
                : 'long';
          nextPositions[ticker] = {
            ticker,
            weight,
            qty,
            price,
            notional,
            side,
            figi: row.figi != null ? String(row.figi) : null,
            instrumentType:
              row.instrument_type != null ? String(row.instrument_type) : null,
            openedAt: prev.positions[ticker]?.openedAt ?? asOf,
            lastUpdatedAt: asOf,
          };
        }
        next.positions = nextPositions;
        next.positionsAsOf = asOf;
      }
      return next;
    }
    case EventType.CYCLE_STARTED: {
      // Mark the pod as "live" -- the building view paints a soft
      // border around windows of pods that are mid-cycle.
      next.cycleInFlight = true;
      next.cycleTraceId = env.trace_id ?? prev.cycleTraceId;
      return next;
    }
    case EventType.AGENT_THINKING_COMPLETED: {
      // Backend explicitly signals end of an LLM call -- clear the
      // ``thinking`` flag without waiting for the matching
      // ``signal_emitted`` (which may never come if the call failed).
      const actor = String(env.data.actor ?? '');
      const agent = prev.agents[actor];
      if (!agent) return next;
      next.agents = { ...prev.agents, [actor]: { ...agent, thinking: false } };
      return next;
    }
    case EventType.DISCIPLINE_GATE_BLOCKED_ORDER: {
      // The discipline gate vetoed an order at submission time --
      // different from ``risk.verdict_received{approved:false}`` which
      // happens earlier in the pipeline.  Surface as an alert so the
      // pod's "N alerts" badge ticks up.
      const ticker =
        typeof env.data.ticker === 'string'
          ? env.data.ticker
          : typeof env.data.symbol === 'string'
            ? env.data.symbol
            : null;
      const reason =
        typeof env.data.reason === 'string'
          ? env.data.reason
          : typeof env.data.rule === 'string'
            ? env.data.rule
            : 'gate_blocked';
      next.alerts = {
        errorCount: prev.alerts.errorCount + 1,
        last: {
          kind: 'gate_blocked',
          ts: env.ts,
          ticker,
          reason,
        },
      };
      return next;
    }
    case EventType.DISCIPLINE_FLAG_RAISED: {
      next.disciplineFlag = {
        code: String(env.data.code ?? 'unknown_flag'),
        severity:
          (env.data.severity as 'low' | 'medium' | 'high' | 'critical') ??
          'medium',
        detail:
          typeof env.data.detail === 'object' && env.data.detail !== null
            ? (env.data.detail as Record<string, unknown>)
            : {},
        raisedAt: env.ts,
      };
      return next;
    }
    case EventType.POSITION_OPENED: {
      const ticker = String(env.data.ticker ?? '').toUpperCase();
      if (!ticker) return next;
      const weight = Number(env.data.weight ?? 0);
      const qty = env.data.qty != null ? Number(env.data.qty) : null;
      next.positions = {
        ...prev.positions,
        [ticker]: {
          ticker,
          weight,
          qty,
          // Streaming ``position.*`` events don't carry price/notional;
          // those land via the broker-snapshot path on ``pod.nav_tick``.
          // Leave them null so the renderer can fall back to weight-only
          // rendering for tickers that don't have a snapshot yet.
          price: null,
          notional: null,
          // Derive side from the most reliable signal we have here.
          // qty sign takes priority over weight sign because qty is the
          // ground truth even when weight rounds to ~0.
          side: (qty != null ? qty : weight) < 0 ? 'short' : 'long',
          figi: null,
          instrumentType: null,
          openedAt: prev.positions[ticker]?.openedAt ?? env.ts,
          lastUpdatedAt: env.ts,
        },
      };
      return next;
    }
    case EventType.POSITION_ADJUSTED: {
      const ticker = String(env.data.ticker ?? '').toUpperCase();
      if (!ticker) return next;
      const existing = prev.positions[ticker];
      // If the backend ships ``weight`` use it directly; otherwise fall
      // back to ``delta`` against the current weight.
      const newWeight =
        env.data.weight != null
          ? Number(env.data.weight)
          : (existing?.weight ?? 0) + Number(env.data.delta ?? 0);
      const newQty =
        env.data.qty != null ? Number(env.data.qty) : existing?.qty ?? null;
      next.positions = {
        ...prev.positions,
        [ticker]: {
          ticker,
          weight: newWeight,
          qty: newQty,
          // Preserve snapshot-derived price/notional if we have them --
          // they'll be refreshed by the next nav_tick.  Adjusting weight
          // doesn't necessarily mean the broker price changed.
          price: existing?.price ?? null,
          notional: existing?.notional ?? null,
          side: (newQty != null ? newQty : newWeight) < 0 ? 'short' : 'long',
          figi: existing?.figi ?? null,
          instrumentType: existing?.instrumentType ?? null,
          openedAt: existing?.openedAt ?? env.ts,
          lastUpdatedAt: env.ts,
        },
      };
      return next;
    }
    case EventType.POSITION_CLOSED: {
      const ticker = String(env.data.ticker ?? '').toUpperCase();
      if (!ticker || !(ticker in prev.positions)) return next;
      const remaining = { ...prev.positions };
      delete remaining[ticker];
      next.positions = remaining;
      return next;
    }
    case EventType.CENTERBOOK_ALLOCATION_CHANGED: {
      const capitalAfter =
        env.data.capital_after != null
          ? Number(env.data.capital_after)
          : env.data.new_capital != null
            ? Number(env.data.new_capital)
            : null;
      if (capitalAfter == null) return next;
      const capitalBefore =
        env.data.capital_before != null
          ? Number(env.data.capital_before)
          : env.data.old_capital != null
            ? Number(env.data.old_capital)
            : null;
      const deltaPct =
        env.data.delta_pct != null
          ? Number(env.data.delta_pct)
          : capitalBefore != null && capitalBefore > 0
            ? (capitalAfter - capitalBefore) / capitalBefore
            : null;
      next.centerbookAllocation = {
        capitalBefore,
        capitalAfter,
        deltaPct,
        reason:
          typeof env.data.reason === 'string' ? env.data.reason : null,
        changedAt: env.ts,
      };
      return next;
    }
    case EventType.CENTERBOOK_OVERLAY_APPLIED: {
      next.centerbook = {
        podScale: Number(env.data.pod_scale ?? 1.0),
        grossBefore:
          env.data.gross_before != null ? Number(env.data.gross_before) : null,
        grossAfter:
          env.data.gross_after != null ? Number(env.data.gross_after) : null,
        appliedAt: env.ts,
      };
      return next;
    }
    case EventType.POLICY_APPLIED:
    case EventType.POLICY_ROLLED_BACK:
    case EventType.POLICY_SHADOW_RECOMMENDED:
    case EventType.POLICY_NO_CHANGE: {
      // All four share a shape: trigger_rule + (optional) param_changed,
      // value_after, audit_id.  Snap the latest into a single slot.
      const kindByType: Record<string, PodPolicyAction['kind']> = {
        [EventType.POLICY_APPLIED]: 'applied',
        [EventType.POLICY_ROLLED_BACK]: 'rolled_back',
        [EventType.POLICY_SHADOW_RECOMMENDED]: 'shadow_recommended',
        [EventType.POLICY_NO_CHANGE]: 'no_change',
      };
      next.policyAction = {
        kind: kindByType[env.type] ?? 'no_change',
        triggerRule: String(env.data.trigger_rule ?? 'unknown'),
        paramChanged:
          typeof env.data.param_changed === 'string'
            ? env.data.param_changed
            : null,
        valueAfter: env.data.value_after,
        auditId:
          typeof env.data.audit_id === 'number' ? env.data.audit_id : null,
        at: env.ts,
      };
      return next;
    }
    case EventType.CYCLE_COMPLETED: {
      // End of cycle: clear thinking flags so UI doesn't leave stale
      // "..." bubbles up until the next cycle starts.
      const clearedAgents: Partial<Record<string, AgentRuntimeState>> = {};
      for (const [key, agent] of Object.entries(prev.agents)) {
        if (!agent) continue;
        clearedAgents[key] = { ...agent, thinking: false };
      }
      next.agents = clearedAgents;
      next.pm = { ...prev.pm, thinking: false };
      next.cycleInFlight = false;
      return next;
    }
    default:
      return next;
  }
}

// One-time replay of the persisted feed through every reducer at
// module load.  We compute pods / atmosphere / celebration / banners /
// leaderboard from the same envelopes the user would have processed
// in the previous session, so a reload restores not just the visible
// feed but the building view's pod state as well.
const _persistedFeed = loadPersistedFeed();
const _hydrated = _rebuildStateFromFeed(_persistedFeed);

export const useDashboardStore = create<DashboardStore>((set, get) => ({
  connection: 'idle',
  lastError: null,
  eventsReceived: _persistedFeed.length,
  feed: _persistedFeed,
  building: null,
  buildingFetchedAt: null,
  pods: _hydrated.pods,
  leaderboard: _hydrated.leaderboard,
  atmosphere: _hydrated.atmosphere,
  celebration: _hydrated.celebration,
  banners: _hydrated.banners,
  view: { kind: 'building' },
  buildingRenderer:
    (typeof window !== 'undefined' &&
      (window.localStorage.getItem('hedgefundweb.buildingRenderer') as BuildingRenderer | null)) ||
    'pixi',
  audioMuted:
    typeof window === 'undefined' ||
    window.localStorage.getItem('hedgefundweb.audioMuted') !== '0',

  setConnection: (connection) => set({ connection }),
  setError: (err) =>
    set({
      lastError: err instanceof Error ? err.message : String(err ?? ''),
    }),
  appendEvent: (envelope) =>
    set((s) => {
      // 0) De-dup by envelope.id.  Two paths can deliver the same id:
      //   * WS reconnect: server's ``read_since(sinceId)`` window can
      //     overlap envelopes we already have in the persisted feed
      //     (cursor and feed drift independently on tab refresh).
      //   * REST resync: ``onResync`` replay can re-feed events the live
      //     socket already pushed when the gap was racy at the edges.
      // Without this guard the dup propagates to every downstream
      // surface (NewsTicker, EventFeed, pod reducers re-applying the
      // same shift twice).
      // Pass the whole envelope (not just ``envelope.id``) so the
      // dedup key picks ``origin_id`` first, then the type-scoped
      // upstream id (``data.event_id`` for news), and only falls
      // back to ``envelope.id`` for events that have no stable
      // upstream handle.  That layering catches the cross-stream
      // duplicate that pure-``id`` dedup misses.
      if (feedHasDuplicateId(s.feed, envelope)) return {};

      // 1) bounded feed
      const nextFeed = s.feed.length >= FEED_MAX ? s.feed.slice(-FEED_MAX + 1) : s.feed.slice();
      nextFeed.push(envelope);

      // 2) per-pod reducer for ``pod:<id>`` subjects
      const podId = podIdFromSubject(envelope.subject);
      const nextPods = { ...s.pods };
      if (podId) {
        const prev = nextPods[podId] ?? emptyPodRuntime(podId);
        nextPods[podId] = reducePodForEvent(prev, envelope);
      }

      // 3) atmosphere overlay (weather + sun_position)
      let nextAtmosphere = reduceAtmosphereForEvent(s.atmosphere, envelope);
      // Camera focus directives from scene_hint.camera -- stamp the
      // target pod so scenes can spotlight it for a couple seconds.
      // Reducer kept inline (rather than in reduceAtmosphereForEvent)
      // because it needs the resolved podId from the envelope's
      // subject, not just the envelope payload.
      const camera = envelope.scene_hint?.camera;
      if (camera && camera !== 'stay' && camera !== 'wide') {
        nextAtmosphere = {
          ...nextAtmosphere,
          cameraFocus: {
            kind: camera,
            podId,
            setAtMs: performance.now(),
          },
        };
      }

      // 3b) Friday celebration overlay
      const nextCelebration = reduceCelebrationForEvent(s.celebration, envelope);

      // 3c) Top-of-page banner slots (session warning + factor crowd)
      const nextBanners = reduceBannersForEvent(s.banners, envelope);

      // 4) leaderboard event
      let nextLeaderboard = s.leaderboard;
      if (envelope.type === EventType.LEADERBOARD_COMPUTED) {
        const d = envelope.data;
        nextLeaderboard = {
          period: String(d.period ?? 'weekly'),
          asOf: String(d.as_of ?? envelope.ts),
          metric: String(d.metric ?? 'utility_avg'),
          rows: (Array.isArray(d.rows) ? d.rows : []).map((row) => {
            const r = row as Record<string, unknown>;
            const nameValue = typeof r.name === 'string' ? r.name : null;
            return {
              pod_id: String(r.pod_id ?? ''),
              rank: Number(r.rank ?? 0),
              value: Number(r.value ?? 0),
              name: nameValue,
              recentDelta: 0,
              changedAtMs: 0,
            };
          }),
        };
      } else if (envelope.type === EventType.LEADERBOARD_RANK_CHANGED && s.leaderboard) {
        // Single-row rank change: stitch the new rank into the live
        // board AND stash a transient ``recentDelta`` so the UI can
        // paint an up/down arrow next to the row for a few seconds.
        const d = envelope.data;
        const podId = String(d.pod_id ?? '');
        const fromRank =
          typeof d.from_rank === 'number' ? Number(d.from_rank) : null;
        const toRank =
          typeof d.to_rank === 'number' ? Number(d.to_rank) : null;
        const value = typeof d.value === 'number' ? Number(d.value) : null;
        const now = performance.now();
        const updated = s.leaderboard.rows.map((row) => {
          if (row.pod_id !== podId) return row;
          const delta = fromRank != null && toRank != null
            ? fromRank - toRank // positive = climbed
            : 0;
          return {
            ...row,
            rank: toRank ?? row.rank,
            value: value ?? row.value,
            recentDelta: delta,
            changedAtMs: now,
          };
        });
        // Keep rows sorted by rank ascending so the top-3 stays correct
        // after a mid-board shuffle.
        const sorted = [...updated].sort((a, b) => a.rank - b.rank);
        nextLeaderboard = { ...s.leaderboard, rows: sorted };
      }

      // Throttled write so a busy cycle (one pm.decision_made
      // followed by 6 agent signals + N orders) doesn't hammer
      // localStorage.  The next reload will pick up the latest
      // persisted snapshot.
      persistFeed(nextFeed);
      return {
        feed: nextFeed,
        eventsReceived: s.eventsReceived + 1,
        pods: nextPods,
        leaderboard: nextLeaderboard,
        atmosphere: nextAtmosphere,
        celebration: nextCelebration,
        banners: nextBanners,
      };
    }),
  setBuilding: (building) =>
    set((s) => {
      // Seed per-pod state from the bootstrap snapshot so the room
      // view has something to show before live events arrive.
      const nextPods = { ...s.pods };
      for (const floor of building.floors) {
        for (const pod of floor.pods) {
          const prev = nextPods[pod.pod_id] ?? emptyPodRuntime(pod.pod_id, pod.floor);
          nextPods[pod.pod_id] = {
            ...prev,
            floor: pod.floor,
            riskState: pod.risk_state,
            haircutPct: pod.haircut_pct,
          };
        }
      }
      return {
        building,
        buildingFetchedAt: new Date().toISOString(),
        pods: nextPods,
      };
    }),
  hydrateFromSnapshot: (snapshot) =>
    set((s) => {
      // First seed the building-level fields (calendar, atmosphere,
      // floor list).  ``setBuilding`` does the floor->pod seeding,
      // so we replicate that loop inline and then layer the rich
      // per-pod snapshot on top.
      const nextPods: typeof s.pods = { ...s.pods };
      for (const floor of snapshot.building.floors) {
        for (const podSummary of floor.pods) {
          const prev =
            nextPods[podSummary.pod_id] ??
            emptyPodRuntime(podSummary.pod_id, podSummary.floor);
          nextPods[podSummary.pod_id] = {
            ...prev,
            floor: podSummary.floor,
            riskState: podSummary.risk_state,
            haircutPct: podSummary.haircut_pct,
          };
        }
      }

      // Now apply each PodSnapshot to its corresponding runtime
      // entry.  Each block defends against missing data with a null
      // check so a fresh pod (no events yet) doesn't blow up the
      // hydration; we simply leave those slots at their defaults.
      for (const [podId, podSnap] of Object.entries(snapshot.pods)) {
        const prev = nextPods[podId] ?? emptyPodRuntime(podId);
        const next: PodRuntimeState = { ...prev };

        // Broker truth from latest nav_tick.
        if (podSnap.cash_balance != null) next.cashBalance = podSnap.cash_balance;
        if (podSnap.gross_exposure != null) next.grossExposure = podSnap.gross_exposure;
        if (podSnap.net_exposure != null) next.netExposure = podSnap.net_exposure;
        if (podSnap.broker_source) next.brokerSource = podSnap.broker_source;
        if (podSnap.positions_as_of) next.positionsAsOf = podSnap.positions_as_of;
        if (Array.isArray(podSnap.positions) && podSnap.positions.length > 0) {
          const positionsMap: Record<string, PodPosition> = {};
          for (const row of podSnap.positions) {
            if (!row.ticker) continue;
            const ticker = row.ticker.toUpperCase();
            const weight = row.weight ?? 0;
            const qty = row.qty ?? null;
            const sideFromQty = qty != null ? qty : weight;
            positionsMap[ticker] = {
              ticker,
              weight,
              qty,
              price: row.price ?? null,
              notional: row.notional ?? null,
              side:
                row.side === 'long' || row.side === 'short'
                  ? row.side
                  : sideFromQty < 0
                    ? 'short'
                    : 'long',
              figi: row.figi ?? null,
              instrumentType: row.instrument_type ?? null,
              openedAt: prev.positions[ticker]?.openedAt ?? podSnap.positions_as_of ?? snapshot.as_of,
              lastUpdatedAt: podSnap.positions_as_of ?? snapshot.as_of,
            };
          }
          next.positions = positionsMap;
        }

        // nav_history -- ring of recent ticks for the sparkline.
        // The backend snapshot's nav_history doesn't carry the broker-
        // scale companion fields (they're per-tick state, not part of
        // the historical ring).  Live ``pod.nav_tick`` events will
        // populate those once the next cycle fires; for now we seed
        // them as null so the type stays consistent.
        if (Array.isArray(podSnap.nav_history) && podSnap.nav_history.length > 0) {
          next.navHistory = podSnap.nav_history.map((entry) => ({
            ts: entry.ts,
            nav: entry.nav,
            intradayReturn: entry.intradayReturn ?? null,
            intradayPnl: entry.intradayPnl ?? null,
            brokerScaleIntradayReturn: null,
            brokerNav: null,
          }));
        }

        // PM decision snapshot.
        if (podSnap.pm) {
          next.pm = {
            ...prev.pm,
            thinking: false,
            lastUiSummary: podSnap.pm.lastUiSummary ?? prev.pm.lastUiSummary,
            lastRationale: podSnap.pm.lastRationale ?? prev.pm.lastRationale,
            confidence: podSnap.pm.confidence,
            targetGross: podSnap.pm.targetGross,
            intentsCount: podSnap.pm.intentsCount,
            decisionAt: podSnap.pm.decisionAt,
            topWeights: podSnap.pm.topWeights.map((w) => ({
              ticker: w.ticker,
              weight: w.weight,
            })),
          };
        }

        // Utility score.  PodUtilityState's required fields don't
        // allow null, so we only populate when the snapshot has the
        // minimum useful contents.
        if (
          podSnap.utility &&
          podSnap.utility.total != null &&
          podSnap.utility.components &&
          podSnap.utility.recordedAt
        ) {
          next.utility = {
            total: podSnap.utility.total,
            components: podSnap.utility.components,
            version: podSnap.utility.version,
            recordedAt: podSnap.utility.recordedAt,
          };
        }

        // Risk state -- only the snapshot fields the building summary
        // didn't already cover.  ``risk_state`` itself comes from
        // ``setBuilding`` above so we don't clobber it here.
        if (podSnap.haircut_pct != null) next.haircutPct = podSnap.haircut_pct;

        // Discipline + centerbook + policy slots: drop straight in.
        if (podSnap.discipline_flag && podSnap.discipline_flag.raisedAt) {
          next.disciplineFlag = {
            code: String(podSnap.discipline_flag.code ?? ''),
            severity:
              (podSnap.discipline_flag.severity as PodDisciplineFlag['severity']) ?? 'low',
            detail: podSnap.discipline_flag.detail ?? {},
            raisedAt: podSnap.discipline_flag.raisedAt,
          };
        }
        if (podSnap.centerbook && podSnap.centerbook.appliedAt) {
          next.centerbook = {
            podScale: podSnap.centerbook.podScale ?? 1,
            grossBefore: podSnap.centerbook.grossBefore,
            grossAfter: podSnap.centerbook.grossAfter,
            appliedAt: podSnap.centerbook.appliedAt,
          };
        }
        if (podSnap.centerbook_allocation && podSnap.centerbook_allocation.appliedAt) {
          next.centerbookAllocation = {
            capitalBefore: podSnap.centerbook_allocation.capitalBefore,
            capitalAfter: podSnap.centerbook_allocation.capitalAfter ?? 0,
            deltaPct: podSnap.centerbook_allocation.deltaPct,
            reason: podSnap.centerbook_allocation.reason,
            // Backend serialises the timestamp under ``appliedAt`` to
            // match the rest of the centerbook payload; the FE store
            // names it ``changedAt`` since the allocation event semantics
            // are "capital reallocation between pods at daily close".
            changedAt: podSnap.centerbook_allocation.appliedAt,
          };
        }
        if (podSnap.policy_action) {
          next.policyAction = {
            kind: podSnap.policy_action.kind,
            triggerRule: String(podSnap.policy_action.triggerRule ?? ''),
            paramChanged: podSnap.policy_action.paramChanged,
            valueAfter: podSnap.policy_action.valueAfter,
            auditId: podSnap.policy_action.auditId,
            at: String(podSnap.policy_action.at ?? ''),
          };
        }

        // Alerts counter.  Map the backend's event-type string into
        // the FE's stricter ``kind`` enum so reducers downstream don't
        // have to know about every wire type variant.
        if (podSnap.alerts) {
          const lastSnap = podSnap.alerts.last;
          let mappedKind: PodAlertState['last'] extends infer L
            ? L extends { kind: infer K }
              ? K
              : never
            : never;
          if (lastSnap) {
            switch (lastSnap.type) {
              case 'order.rejected':
                mappedKind = 'order_rejected';
                break;
              case 'order.retried':
                mappedKind = 'order_retried';
                break;
              case 'discipline.gate_blocked_order':
                mappedKind = 'gate_blocked';
                break;
              default:
                // Unknown alert type from a newer backend -- conservatively
                // bucket as ``risk_veto`` so the UI still surfaces it.
                mappedKind = 'risk_veto';
            }
          } else {
            // ``last`` is null -- type doesn't matter, we won't construct it.
            mappedKind = 'risk_veto';
          }
          next.alerts = {
            errorCount: podSnap.alerts.errorCount,
            last: lastSnap
              ? {
                  kind: mappedKind,
                  ts: lastSnap.ts,
                  ticker: lastSnap.ticker,
                  reason: lastSnap.reason ?? 'alert',
                }
              : null,
          };
        }

        // Agent state: phrases + signals are keyed by actor.  Merge
        // into the existing agents map so the snapshot doesn't drop
        // any agent that the FE has already seen via live events
        // since the snapshot was taken.
        if (podSnap.agent_phrases || podSnap.agent_signals) {
          const nextAgents = { ...prev.agents };
          const actorIds = new Set([
            ...Object.keys(podSnap.agent_phrases ?? {}),
            ...Object.keys(podSnap.agent_signals ?? {}),
          ]);
          for (const actor of actorIds) {
            const existing = nextAgents[actor];
            const phrase = podSnap.agent_phrases?.[actor];
            const signal = podSnap.agent_signals?.[actor];
            nextAgents[actor] = {
              actor,
              thinking: false,
              lastSignal: signal?.signal ?? existing?.lastSignal ?? null,
              lastConfidence: signal?.confidence ?? existing?.lastConfidence ?? null,
              // ``topTickers`` doubles as the bubble text for ambient
              // entries; if we have a phrase we put it there too so
              // the agent's speech bubble has content right after
              // reload instead of being empty until the next signal.
              topTickers: signal?.topTickers ?? (phrase ? [phrase.text] : existing?.topTickers ?? []),
              mood: phrase?.mood ?? existing?.mood ?? null,
              intensity: existing?.intensity ?? null,
              lastUpdatedAt: phrase?.ts ?? signal?.ts ?? existing?.lastUpdatedAt ?? null,
              latencyMs: signal?.latencyMs ?? existing?.latencyMs ?? null,
              lastPhrase: phrase?.text ?? existing?.lastPhrase ?? null,
              lastPhraseAt: phrase?.ts ?? existing?.lastPhraseAt ?? null,
              lastPhraseKind: phrase?.kind ?? existing?.lastPhraseKind ?? null,
            };
          }
          next.agents = nextAgents;
        }

        nextPods[podId] = next;
      }

      return {
        building: snapshot.building,
        buildingFetchedAt: snapshot.as_of,
        pods: nextPods,
      };
    }),
  selectPod: (podId) => {
    if (!podId) return;
    if (!get().pods[podId]) {
      set((s) => ({ pods: { ...s.pods, [podId]: emptyPodRuntime(podId) } }));
    }
    set({ view: { kind: 'pod_room', podId } });
  },
  goToBuilding: () => set({ view: { kind: 'building' } }),
  setBuildingRenderer: (r) => {
    try {
      window.localStorage.setItem('hedgefundweb.buildingRenderer', r);
    } catch {
      /* localStorage disabled */
    }
    set({ buildingRenderer: r });
  },
  setAudioMuted: (muted) => {
    try {
      window.localStorage.setItem('hedgefundweb.audioMuted', muted ? '1' : '0');
    } catch {
      /* localStorage disabled */
    }
    set({ audioMuted: muted });
  },
  reset: () => {
    // ``reset`` is the user-facing "start fresh" action -- clear the
    // persisted feed too so the next reload doesn't repopulate from
    // the cache we just wanted to discard.
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.removeItem(FEED_STORAGE_KEY);
      } catch {
        /* no-op */
      }
    }
    lastFeedPersistAt = 0;
    set({
      connection: 'idle',
      lastError: null,
      eventsReceived: 0,
      feed: [],
      building: null,
      buildingFetchedAt: null,
      pods: {},
      leaderboard: null,
      atmosphere: { ...EMPTY_ATMOSPHERE },
      celebration: { ...EMPTY_CELEBRATION },
      banners: { ...EMPTY_BANNERS },
      view: { kind: 'building' },
    });
  },
}));

export const SIGNAL_BUBBLE_TTL_MS = SIGNAL_BUBBLE_MAX_AGE_MS;
export const PM_BUBBLE_TTL_MS = PM_BUBBLE_MAX_AGE_MS;
